import { createContext, useContext, ReactNode, useMemo } from "react";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { useCloneTouchpointModal } from "../hooks/modals/use-clone-touchpoint-modal";
import { useArchiveTouchpointModal } from "../hooks/modals/use-archive-touchpoint-modal";
import { useAddTouchpointToFolderModal } from "../hooks/modals/use-add-touchpoint-to-folder-modal";
import { useAddTouchpointToBatchModal } from "../hooks/modals/use-add-touchpoint-to-batch-modal";
import { useRollToNextFYModal } from "../hooks/modals/use-roll-to-next-fy-modal";
import { useMoveTouchpointsToCampaignModal } from "../hooks/modals/use-move-touchpoints-to-campaign-modal";
import { useRewriteEmailModal } from "../hooks/modals/use-rewrite-email-modal";

/**
 * Context for touchpoint actions (Clone, Archive, Folder, Batch, Campaign) so modals
 * don't close when table rows remount.
 */
interface TouchpointActionContextType {
  onSuccess: () => void;
  openCloneModal: (touchpoint: TouchpointAttributes) => void;
  openArchiveModal: (touchpoints: TouchpointAttributes | TouchpointAttributes[]) => void;
  openAddToFolderModal: (touchpoints: TouchpointAttributes | TouchpointAttributes[]) => void;
  openAddBatchModal: (touchpoints: TouchpointAttributes | TouchpointAttributes[]) => void;
  openRollToNextFYModal: (touchpoints: TouchpointAttributes | TouchpointAttributes[]) => void;
  openMoveToCampaignModal: (touchpoints: TouchpointAttributes | TouchpointAttributes[]) => void;
  openRewriteEmailModal: (touchpoint: TouchpointAttributes) => void;
}

const TouchpointActionContext = createContext<TouchpointActionContextType | undefined>(undefined);

interface TouchpointActionProviderProps {
  children: ReactNode;
  onSuccess: () => void;
}

export const TouchpointActionProvider = ({ children, onSuccess }: TouchpointActionProviderProps) => {
  const { openCloneTouchpointModal, cloneTouchpointModalMarkup } = useCloneTouchpointModal(onSuccess);
  const { openArchiveModal, archiveModalMarkup } = useArchiveTouchpointModal({ onSuccess });
  const { openAddToFolderModal, addToFolderModalMarkup } = useAddTouchpointToFolderModal({
    onSuccess,
  });
  const { openAddBatchModal, addBatchModalMarkup } = useAddTouchpointToBatchModal({ onSuccess });
  const { openRollToNextFYModal, rollToNextFYModalMarkup } = useRollToNextFYModal({ onSuccess });
  const { openMoveToCampaignModal, moveToCampaignModalMarkup } = useMoveTouchpointsToCampaignModal({
    onSuccess,
  });
  const { openRewriteEmailModal, rewriteEmailModalMarkup } = useRewriteEmailModal();

  const value = useMemo(
    () => ({
      onSuccess,
      openCloneModal: openCloneTouchpointModal,
      openArchiveModal,
      openAddToFolderModal,
      openAddBatchModal,
      openRollToNextFYModal,
      openMoveToCampaignModal,
      openRewriteEmailModal,
    }),
    [
      onSuccess,
      openCloneTouchpointModal,
      openArchiveModal,
      openAddToFolderModal,
      openAddBatchModal,
      openRollToNextFYModal,
      openMoveToCampaignModal,
      openRewriteEmailModal,
    ],
  );

  return (
    <TouchpointActionContext.Provider value={value}>
      {children}
      {cloneTouchpointModalMarkup}
      {archiveModalMarkup}
      {addToFolderModalMarkup}
      {addBatchModalMarkup}
      {rollToNextFYModalMarkup}
      {moveToCampaignModalMarkup}
      {rewriteEmailModalMarkup}
    </TouchpointActionContext.Provider>
  );
};

export const useTouchpointActionContext = () => {
  const context = useContext(TouchpointActionContext);
  if (!context) {
    throw new Error("useTouchpointActionContext must be used within a TouchpointActionProvider");
  }
  return context;
};
